import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import { useUserSettings } from "../../../context/userSettingsContext";
import useDataInitializer from "../../../hooks/useDataInitializer";
import { useDebounce } from "../../../hooks/useDebounce";
import useListFilters from "../../../hooks/useListFilters";
import { useI18n } from "../../../hooks/useTranslations";
import { useDefaultTasksApi } from "../../../http/defaultTasks/api";
import { DefaultTask } from "../../../http/defaultTasks/types";
import { PaginationQueryWithFields } from "../../../types";
import { Action, getDeleteAction, getEditAction } from "../../ui/ListItemMenu";
import ModalDialog from "../../ui/ModalDialog";
import Table from "../../ui/Table";
import CreateDefaultTaskButton from "./CreateDefaultTaskButton";
import DeleteDefaultTaskDialog from "./DeleteDefaultTaskDialog";
import EditDefaultTaskDialog from "./EditDefaultTaskDialog";

const DEFAULT_TASKS_FIELDS = ["id", "name", "isSystemTask"] as const;
type DefaultTaskField = (typeof DEFAULT_TASKS_FIELDS)[number];
type DefaultTaskSearch = Pick<DefaultTask, DefaultTaskField>;

interface DefaultTasksProps {
  onClose: () => void;
}

const DefaultTasks: FC<DefaultTasksProps> = ({ onClose }) => {
  const i18n = useI18n();
  const userSettings = useUserSettings();
  const defaultTasksApi = useDefaultTasksApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());

  const [itemToEdit, setItemToEdit] = useState<DefaultTaskSearch>();
  const [itemToDelete, setItemToDelete] = useState<DefaultTaskSearch>();

  const query: PaginationQueryWithFields<DefaultTaskSearch> = {
    ...filters.state,
    q: debouncedValue,
    fields: DEFAULT_TASKS_FIELDS,
  };

  const result = defaultTasksApi.useAdminDefaultTasks(query, aborter.current.signal);
  const [data] = useDataInitializer(result.data, result.error);

  useEffect(() => {
    result.error && dispatch(showErrorNotification(i18n, result.error));
  }, [result.error]);

  const getRowActions = (item: DefaultTaskSearch): Action[] => {
    const actions: Action[] = [];

    if (userSettings.permissions.defaultTask.update) {
      actions.push(getEditAction(i18n, () => setItemToEdit(item)));
    }

    if (userSettings.permissions.defaultTask.delete) {
      actions.push(getDeleteAction(i18n, () => setItemToDelete(item), true));
    }

    return actions;
  };

  return (
    <>
      <ModalDialog
        onClose={onClose}
        title={i18n.t.defaultTasks.plural}
        preTitleButton={userSettings.permissions.defaultTask.insert && <CreateDefaultTaskButton />}
      >
        <Table<DefaultTaskSearch>
          filters={filters}
          head={
            <>
              <th align="left">{i18n.t.common.name}</th>
              {(userSettings.permissions.defaultTask.update || userSettings.permissions.defaultTask.delete) && (
                <th></th>
              )}
            </>
          }
          onRowClick={(item, event) => {
            event.preventDefault();
            setItemToEdit(item);
          }}
          renderRow={(item) => (
            <>
              <td>{item.name}</td>
            </>
          )}
          rowActions={getRowActions}
          error={result.error}
          items={data?.items}
          total={data?.total}
        />
      </ModalDialog>

      {!!itemToDelete && (
        <DeleteDefaultTaskDialog defaultTaskId={itemToDelete.id} onClose={() => setItemToDelete(undefined)} />
      )}

      {!!itemToEdit && <EditDefaultTaskDialog taskId={itemToEdit.id} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

export default DefaultTasks;
